<template>
  <RevForm :initial-values :validate="validate()">
    <template #default="{ values, errors, isValid }">
      <RevDrawer
        :back-button-label="i18n(translations.backButtonLabel)"
        :has-back-button="currentStep === DSA_FORM_STEPS.CONTACT_DETAILS"
        :name="FORM_MODAL_NAME"
        :title="i18n(translations.title)"
        @back="currentStep = DSA_FORM_STEPS.PRODUCT_ORIGIN"
      >
        <template #body>
          <div v-if="currentStep === DSA_FORM_STEPS.PRODUCT_ORIGIN">
            <h2 class="heading-2 mb-8">
              {{ i18n(translations.productOriginQuestion) }}
            </h2>

            <div class="flex flex-col gap-16">
              <RevRadio
                v-for="action in actions"
                :id="action.id"
                :key="action.id"
                v-model="selectedAction"
                :value="action.id"
                variant="full"
              >
                <template #label> {{ action.label }}</template>
              </RevRadio>
            </div>
          </div>

          <div
            v-if="
              currentStep === DSA_FORM_STEPS.CONTACT_DETAILS &&
              selectedAction === DSA_FORM_TYPES.SOME_OR_ALL_NON_EU
            "
            class="mt-8 space-y-32"
          >
            <div>
              <h3 class="heading-3 mb-4">
                {{ i18n(translations.businessName) }}
              </h3>

              <RevInputText
                id="businessName"
                v-model="values.businessName"
                :error="errors.businessName"
                :label="i18n(translations.businessName)"
                :placeholder="i18n(translations.businessName)"
              />
            </div>

            <div>
              <h3 class="heading-3 mb-4">
                {{ i18n(translations.businessAddress) }}
              </h3>
              <div class="flex flex-col gap-4">
                <AddressFields
                  v-model:city="values.city"
                  v-model:country="values.country"
                  v-model:postal-code="values.postalCode"
                  v-model:state-or-province="values.stateOrProvince"
                  v-model:street="values.street"
                  v-model:street2="values.street2"
                  :company-field-label-message="i18n(translations.businessName)"
                  :default-country
                  :errors
                  :feature-code="FeatureCode.WEB_MERCHANT_PROFILE_DSA_BLOCK"
                />
              </div>
            </div>

            <div class="space-y-12">
              <h3 class="heading-3 mb-4">
                {{ i18n(translations.contactDetails) }}
              </h3>

              <RevInputPhone
                id="phone"
                v-model="values.phone"
                data-cs-mask
                :default-country="referenceMarket"
                :description="i18n(translations.phoneNumberDescription)"
                :error="errors.phone"
                :label="i18n(translations.phoneNumber)"
                :translate="i18n.country"
              />

              <div class="body-1-bold">
                {{ i18n(translations.emailOrWebsiteNote) }}
              </div>

              <RevInputText
                id="email"
                v-model="values.email"
                :description="i18n(translations.emailDescription)"
                :disabled="values.website !== ''"
                :error="errors.email"
                :label="i18n(translations.email)"
              />

              <RevInputText
                id="website"
                v-model="values.website"
                :disabled="values.email !== ''"
                :error="errors.website"
                :label="i18n(translations.website)"
              />
            </div>

            <p class="body-2">
              {{ i18n(translations.complianceDisclaimer) }}
            </p>
          </div>
        </template>

        <template #footer>
          <RevButton
            :disabled="!canSubmit(values, isValid)"
            full-width="always"
            :loading="isSubmitting"
            variant="primary"
            @click="handleSubmit(values)"
          >
            {{
              currentStep === DSA_FORM_STEPS.CONTACT_DETAILS
                ? i18n(translations.addContactButton)
                : i18n(translations.continue)
            }}
          </RevButton>
        </template>
      </RevDrawer>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { Country, sellerProfileAPI } from '@backmarket/http-api'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInputPhone } from '@ds/components/InputPhone'
import { RevInputText } from '@ds/components/InputText'
import { closeModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'

import { REGEXP_VALID_EMAIL, REGEXP_VALID_URL } from '~/constants/regex'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import {
  DSA_FORM_STEPS,
  DSA_FORM_TYPES,
  type DsaFormValues,
  FORM_MODAL_NAME,
} from './AddContactFormModal.constants'
import translations from './AddContactFormModal.translations'

const emit = defineEmits<{
  'refresh-dsa-contact-details': []
}>()

const { openErrorToast, openSuccessToast } = useTheToast()

const { referenceMarket } = useSellerInfoStore()

const i18n = useI18n()

const defaultCountry = computed(() => referenceMarket as unknown as Country)

const currentStep = ref(DSA_FORM_STEPS.PRODUCT_ORIGIN)
const isSubmitting = ref(false)
const selectedAction = ref<string | boolean>(false)
const actions = [
  {
    id: DSA_FORM_TYPES.ALL_EU,
    label: i18n(translations.productOriginEU),
  },
  {
    id: DSA_FORM_TYPES.SOME_OR_ALL_NON_EU,
    label: i18n(translations.productOriginNonEU),
  },
]

const initialValues = ref<DsaFormValues>({
  businessName: '',
  city: '',
  country: '',
  email: '',
  phone: '',
  postalCode: '',
  stateOrProvince: '',
  street2: '',
  street: '',
  website: '',
})

function validate() {
  return makeValidate<DsaFormValues>({
    ...AddressFieldsValidators(i18n),
    businessName: { required: required(i18n(translations.required)) },
    phone: { required: required(i18n(translations.required)) },
    email: {
      matchingRegExp: matchingRegExp(
        REGEXP_VALID_EMAIL,
        i18n(translations.emailInvalid),
      ),
    },
    website: {
      matchingRegExp: matchingRegExp(
        REGEXP_VALID_URL,
        i18n(translations.websiteInvalid),
      ),
    },
  })
}

function canSubmit(values: DsaFormValues, isValid: boolean) {
  if (
    currentStep.value === DSA_FORM_STEPS.PRODUCT_ORIGIN &&
    !selectedAction.value
  )
    return false

  if (currentStep.value === DSA_FORM_STEPS.CONTACT_DETAILS) {
    return isValid && Boolean(values.website || values.email)
  }

  return true
}

async function postDsaContactDetails(body: Record<string, unknown>) {
  try {
    isSubmitting.value = true

    await $httpFetch(sellerProfileAPI.postDsaContactDetails, {
      body,
    })

    openSuccessToast({
      content: i18n(translations.success),
    })

    emit('refresh-dsa-contact-details')
    closeModal(FORM_MODAL_NAME)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}

async function handleSubmit(values: DsaFormValues) {
  if (
    currentStep.value === DSA_FORM_STEPS.PRODUCT_ORIGIN &&
    selectedAction.value === DSA_FORM_TYPES.ALL_EU
  ) {
    await postDsaContactDetails({ contactDetails: null })
  } else if (
    currentStep.value === DSA_FORM_STEPS.PRODUCT_ORIGIN &&
    selectedAction.value === DSA_FORM_TYPES.SOME_OR_ALL_NON_EU
  ) {
    currentStep.value = DSA_FORM_STEPS.CONTACT_DETAILS
  } else if (currentStep.value === DSA_FORM_STEPS.CONTACT_DETAILS) {
    await postDsaContactDetails({
      contactDetails: {
        ...values,
        addressLine1: values.street,
        addressLine2: values.street2,
      },
    })
  }
}
</script>
